import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, Input, EventEmitter, Output, ViewContainerRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { forkJoin, Subject, of } from 'rxjs';
import { catchError, takeUntil, debounceTime, switchMap, map } from 'rxjs/operators';
import * as Cesium from 'cesium';
import * as _ from 'lodash';
import { User } from '../../../../core/models/organization.interface';
import { CameraService } from '../../../../core/services/camera.service';
import { CoreService } from '../../../../core/services/core.service';
import { OrganizationService } from '../../../../core/services/organization.service';
import { WindowService } from '../../../../core/services/window.service';
import { SessionOptionsService } from '../../../../core/services/session-option.service';
import { GlobalSettingsService } from '../../../../core/services/global-settings.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DetailsModalComponent } from '../details-modal/details-modal.component';
import { MultipleMessageComponent } from '../multiple-message/multiple-message.component';
import { MessageComponent } from '../../message/message.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { PhoneboxComponent } from '../../phonebox/phonebox.component';
// import { MultipleMessageComponent } from '../multiple-message/multiple-message.component';
interface LocationItem {
  name: string;
  code: string;
  display: string;
  latLonRange?: {
    maxLat: number;
    minLat: number;
    maxLon: number;
    minLon: number;
  };
}

type CountryListItem = LocationItem;
type StateListItem = LocationItem;
type CityListItem = LocationItem;

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [FormsModule, ScrollingModule],
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.css'],
})
export class UsersListComponent implements OnInit, OnDestroy, AfterViewInit {
  private lastSelectedOrgsLength = 0;
  private lastOrgTreeItemListLength = 0;
  @Input() users: User[] = [];
  @Input() phoneNumbers: User[] = [];
  @ViewChild('searchInput') searchInput!: ElementRef;
  @Output() userInfoRequested = new EventEmitter<User>();
  @Input() geofenceResults: any;
  @ViewChild('phoneboxContainer', { read: ViewContainerRef })
  phoneboxContainer!: ViewContainerRef;
  // Data properties
  dataItems: any[] = [];
  dataItemsNew: any[] = [];
  selectedCompany = 'All Companies';
  companyList: string[] = [];
  allUserItems: User[] = [];
  allUserItemsFiltered: User[] = [];
  allUserItemsFilteredByLocation: User[] = [];
  allUserItemsFilteredByCompany: User[] = [];
  filteredUsers: User[] = [];

  // Location filters
  countryList: CountryListItem[] = [];
  stateList: StateListItem[] = [
    { name: 'All states', code: 'All states', display: 'All States' },
  ];
  cityList: CityListItem[] = [
    { name: 'All cities', code: 'All cities', display: 'All cities' },
  ];
  selectedState = this.stateList[0];
  selectedCity = this.cityList[0];
  selectedCountry?: CountryListItem;

  // Search
  orgTreeSearch = '';
  query = '';
  userInfo: any;
  // Permissions
  private hasShowHomeLocations = false;
  private hasSms = false;
  private hasVoice = false;

  // UI Config
  config = {
    theme: 'light',
    axis: 'y',
    setHeight: '75vh',
    scrollInertia: 0,
    setWidth: '275px',
  };

  private lastOrgId?: string;
  private userLocationMarker?: Cesium.Entity;
  private destroy$ = new Subject<void>();
  userCount: any;
  private searchTerm$ = new Subject<string>();

  constructor(
    private sessionOptionsService: SessionOptionsService,
    // private messagingService: MessagingService,
    private globalSettings: GlobalSettingsService,
    private windowService: WindowService,
    private coreService: CoreService,
    private modalService: NgbModal,
    private organizationService: OrganizationService,
    private camera: CameraService
  ) { }

  ngOnInit(): void {
    this.setPermissions();
    this.initiateCountryList();
    this.setData();
    // Debounced search subscription
    this.searchTerm$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(term => {
        this.applySearch(term);
      });
  }

  ngDoCheck(): void {
    const selectedOrgsLength = this.globalSettings.getSelectedOrgs()?.length || 0;
    if (selectedOrgsLength !== this.lastSelectedOrgsLength) {
      this.lastSelectedOrgsLength = selectedOrgsLength;
      this.filterUsersByOrgs();
    }

    const orgTreeItemListLength = this.globalSettings.getOrgTreeItemList()?.length || 0;
    if (orgTreeItemListLength !== this.lastOrgTreeItemListLength) {
      this.lastOrgTreeItemListLength = orgTreeItemListLength;
      this.filterUsersByOrgTree();
    }
  }
  filterUsersByOrgTree(): void {
    if (!this.allUserItems) {
      return;
    }

    const orgTreeItemList = this.globalSettings.getOrgTreeItemList();
    const allOrgIds: string[] = [];
    orgTreeItemList.forEach((org: any) => {
      if (org.type === 'org') {
        allOrgIds.push(org.organization.id);
      }
    });

    let numberOfAllOrgs = 0;
    const orgTreeItemListAll = this.globalSettings.getOrgTreeItemListAll?.() || [];
    orgTreeItemListAll.forEach((item: any) => {
      if (item.type === 'org') {
        numberOfAllOrgs++;
      }
    });

    if (allOrgIds.length === 0) {
      this.allUserItemsFiltered = [];
      Promise.resolve().then(() => {
        this.globalSettings.setNumberOfUsersFiltered(this.allUserItemsFiltered.length);
      });
      return;
    }

    if (allOrgIds.length === numberOfAllOrgs) {
      this.allUserItemsFiltered = this.sortUsers(this.allUserItems);
      Promise.resolve().then(() => {
        this.globalSettings.setNumberOfUsersFiltered(this.allUserItemsFiltered.length);
      });
      return;
    }

    const users = this.allUserItems.filter(user =>
      this.isUserInOrgTree(user, allOrgIds) && this.isInGeo(user)
    );

    this.allUserItemsFiltered = this.sortUsers(users);
    Promise.resolve().then(() => {
      this.globalSettings.setNumberOfUsersFiltered(this.allUserItemsFiltered.length);
    });
  }
  ngOnChanges(): void {
    if (this.users) {
      this.allUserItems = this.users;
      this.allUserItemsFiltered = this.users;
      this.allUserItemsFiltered = this.sortUsers(this.allUserItems);

      this.phoneNumbers = this.phoneNumbers;

      // Get unique company names
      this.companyList = [
        'All Companies',
        ...new Set(
          this.users
            .filter(
              (user) => user.companyName && typeof user.companyName === 'string'
            )
            .map((user) => user.companyName as string)
        ),
      ];
    }
  }

  hasMobilePhone(userId: string): boolean {
    return this.phoneNumbers?.some(
      (ph: any) => ph.userId === userId && ph.number
    ) ?? false;
  }

  ngAfterViewInit(): void {
    this.countryList = this.globalSettings.getCountryList();
  }
  getNumberOfUsersFilter(): number {
    return this.globalSettings.getNumberOfUsersFiltered();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeUserLocationMarker();
  }

  // Permission methods
  private setPermissions(): void {
    this.sessionOptionsService.getArcliveActions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(actions => {
        this.hasShowHomeLocations = actions.includes('arclive.feature.showHomeLocations');
        this.hasSms = actions.includes('arclive.communication.sms');
        this.hasVoice = actions.includes('arclive.communication.voice');
      });
  }

  hasShowHomeLocationsPermission(): boolean {
    return this.hasShowHomeLocations;
  }
  hasSmsPermission(): boolean {
    return this.hasSms;
  }
  hasVoicePermission(): boolean {
    return this.hasVoice;
  }

  // Company methods
  onCompanyChange(): void {
    if (this.selectedCompany === 'All Companies') {
      this.allUserItemsFilteredByCompany = this.allUserItems;
      return;
    }
    this.allUserItemsFilteredByCompany = this.allUserItems.filter(
      user => user.companyName === this.selectedCompany
    );
  }

  // Location filter methods
  countryChanged(): void {
    this.selectedState = this.stateList[0];
    if (this.selectedCountry) {
      this.getStateList(this.selectedCountry.code);
      this.getCityList(this.selectedCountry.code, this.selectedState.code);
    }
    this.filterUsersByLocation();
    this.zoomToLocation(this.selectedCountry?.latLonRange);
  }

  stateChanged(): void {
    this.selectedCity = this.cityList[0];
    if (this.selectedCountry) {
      this.getCityList(this.selectedCountry.code, this.selectedState.code);
    }
    this.filterUsersByLocation();
    this.zoomToLocation(this.selectedState.latLonRange);
  }

  cityChanged(): void {
    this.filterUsersByLocation();
    this.zoomToLocation(this.selectedCity.latLonRange);
  }
  // Helper to check if user is in orgs
  isUserInOrgs(orgs: any[], userCompany: string | undefined): boolean {
    return orgs.some(org => org.companyName === (userCompany ?? ''));
  }

  // Main filter function
  filterUsersByOrgs(): void {
    const selectedOrgs = this.globalSettings.getSelectedOrgs?.() || [];
    const allCompanies = this.companyList || [];
    // TODO: Replace with actual logic to determine if org is Global Guardian
    const isGlobalGuardian = false; // Set this based on your app's logic

    if (isGlobalGuardian) {
      if (!this.allUserItems) return;

      if (allCompanies.length === selectedOrgs.length) {
        this.allUserItemsFiltered = this.sortUsers(this.allUserItems);
      } else if (selectedOrgs.length === 0) {
        this.allUserItemsFiltered = [];
      } else {
        this.allUserItemsFiltered = this.sortUsers(
          this.allUserItems.filter(item =>
            this.isUserInOrgs(selectedOrgs, item.companyName ?? '')
          )
        );
      }
    } else {
      if (!(this.allUserItems && this.selectedCountry)) return;

      if (
        this.selectedCountry.code.toLowerCase() === 'all' &&
        allCompanies.length === selectedOrgs.length
      ) {
        this.allUserItemsFiltered = this.sortUsers(this.allUserItems);
      } else if (selectedOrgs.length === 0) {
        this.allUserItemsFiltered = [];
      } else {
        this.allUserItemsFiltered = this.sortUsers(
          this.allUserItems.filter(item =>
            this.isUserInOrgs(selectedOrgs, item.companyName ?? '') && this.isInGeo(item)
          )
        );
      }
    }

    Promise.resolve().then(() => {
      this.globalSettings.setNumberOfUsersFiltered(this.allUserItemsFiltered.length);
    });
  }
  private filterUsersByLocation(): void {
    const orgTreeItemList = this.globalSettings.getOrgTreeItemList();
    const allOrgIds = orgTreeItemList
      .filter(org => org.type === 'org')
      .map(org => org.organization.id);

    this.allUserItemsFiltered = this.sortUsers(
      this.allUserItems.filter(
        item => this.isInGeo(item) && this.isUserInOrgTree(item, allOrgIds)
      )
    );
    this.globalSettings.setNumberOfUsersFiltered(this.allUserItemsFiltered.length);
  }

  private isInGeo(item: User): boolean {
    if (!this.selectedCountry) return true;
    const countryOk = this.selectedCountry.code.toLowerCase() === 'all' || item.geo?.country === this.selectedCountry.code;
    const stateOk = this.selectedState.code.toLowerCase() === 'all states' || item.geo?.region === this.selectedState.code;
    const cityOk = this.selectedCity.code.toLowerCase() === 'all cities' || item.geo?.place === this.selectedCity.code;
    return countryOk && stateOk && cityOk;
  }

  private isUserInOrgTree(user: User, allOrgIds: string[]): boolean {
    return _.intersection(allOrgIds, user.orgIds || []).length > 0;
  }

  openPhoneWindow(event: Event, user: User): void {
    // Prevent default click behavior if needed
    event.preventDefault();
    event.stopPropagation();

    const modalRef = this.modalService.open(PhoneboxComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });

    // set inputs for the PhoneboxComponent
    modalRef.componentInstance.user = user;
    modalRef.componentInstance.phone = user.phones?.[0]?.number ?? '';
    modalRef.componentInstance.toUserFullName = user.name ?? '';
    modalRef.componentInstance.toCompanyName = user.companyName ?? '';
  }

  zoomToMap(event: Event, user: User): void {
    event.stopPropagation();
    const geo = user.geo;
    if (geo && geo.position && Array.isArray(geo.position.coordinates)) {
      const lat = geo.position.coordinates[1];
      const lng = geo.position.coordinates[0];
      this.camera.flyToLocation(lat, lng, 2000);
      this.setUserLocationMarker(lat, lng, user.name);
    }
  }

  private setUserLocationMarker(lat: number, lng: number, name: string): void {
    this.removeUserLocationMarker();
    this.userLocationMarker = this.coreService.getViewer().entities.add({
      position: Cesium.Cartesian3.fromDegrees(lng, lat, 0),
      name: 'User Location Marker',
      show: true,
      point: {
        pixelSize: 10,
        color: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 2,
      },
      label: {
        text: name,
        font: '14pt helvetica',
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        outlineWidth: 2,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -9),
      },
    });
    setTimeout(() => {
      this.removeUserLocationMarker();
    }, 60000);
  }

  private removeUserLocationMarker(): void {
    if (this.userLocationMarker) {
      this.coreService.getViewer().entities.remove(this.userLocationMarker);
    }
  }

  private zoomToLocation(latLonRange?: {
    maxLat: number;
    minLat: number;
    maxLon: number;
    minLon: number;
  }): void {
    if (latLonRange) {
      const rectangle = Cesium.Rectangle.fromDegrees(
        latLonRange.minLon,
        latLonRange.minLat,
        latLonRange.maxLon,
        latLonRange.maxLat
      );
      this.coreService.getViewer().camera.flyTo({
        destination: rectangle
      });
    }
  }

  private sortUsers(users: User[]): User[] {
    return _.sortBy(users, (u) => u.name?.toLowerCase() || '');
  }

  // Private helper methods
  private setupSubscriptions(): void {
    // Setup any necessary subscriptions
  }

  private initiateCountryList(): void {
    const countryList = this.globalSettings.getCountryList();
    if (countryList.length > 0) {
      this.countryList = countryList;
      this.selectedCountry = this.countryList[0];
    }
  }

  private getStateList(countryCode: string): void {
    const homeLocationsAll = this.globalSettings.getHomeLocationsAll();
    const stateList = _.chain(homeLocationsAll)
      .filter((loc) => loc.country === countryCode && loc.region !== '')
      .filter(
        (loc) =>
          countryCode !== 'US' ||
          (countryCode === 'US' && loc.region.length === 2)
      )
      .map((loc) => loc.region)
      .uniq()
      .sort()
      .value();

    const num: { [key: string]: number } = {};
    const latlon: { [key: string]: any[] } = {};
    stateList.forEach((code) => {
      num[code] = 0;
      latlon[code] = [];
    });

    homeLocationsAll.forEach((loc) => {
      if (
        loc.country === countryCode &&
        loc.region !== '' &&
        (loc.country !== 'US' ||
          (loc.country === 'US' && loc.region.length === 2))
      ) {
        num[loc.region]++;
        latlon[loc.region].push(loc.position);
      }
    });

    const latLonRange: { [key: string]: any } = {};
    Object.entries(latlon).forEach(([key, data]) => {
      const latArray = data.map((item) => item.coordinates[1]);
      const lonArray = data.map((item) => item.coordinates[0]);

      let maxLat = _.max(latArray);
      let minLat = _.min(latArray);
      let maxLon = _.max(lonArray);
      let minLon = _.min(lonArray);

      if (maxLat === minLat) {
        maxLat += 1;
        minLat -= 1;
      }
      if (maxLon === minLon) {
        maxLon += 0.1;
        minLon -= 0.1;
      }

      latLonRange[key] = { maxLat, minLat, maxLon, minLon };
    });

    const list: Array<StateListItem> = [
      { name: 'All states', code: 'All states', display: 'All states' },
    ];
    stateList.forEach((code) => {
      const display = `${code} (${num[code]})`;
      list.push({ name: code, code, display, latLonRange: latLonRange[code] });
    });

    this.stateList = list;
    this.selectedState = this.stateList[0];
  }

  private getCityList(countryCode: string, stateCode: string): void {
    const homeLocationsAll = this.globalSettings.getHomeLocationsAll();
    const cityList = _.chain(homeLocationsAll)
      .filter(
        (loc) =>
          loc.country === countryCode &&
          loc.region === stateCode &&
          loc.place &&
          loc.place.length > 0
      )
      .map((loc) => loc.place)
      .uniq()
      .sort()
      .value();

    const num: { [key: string]: number } = {};
    const latlon: { [key: string]: any[] } = {};
    cityList.forEach((city) => {
      num[city] = 0;
      latlon[city] = [];
    });

    homeLocationsAll.forEach((loc) => {
      if (
        loc.country === countryCode &&
        loc.region === stateCode &&
        loc.place &&
        loc.place.length > 0
      ) {
        num[loc.place]++;
        latlon[loc.place].push(loc.position);
      }
    });

    const latLonRange: { [key: string]: any } = {};
    Object.entries(latlon).forEach(([key, data]) => {
      const latArray = data.map((item) => item.coordinates[1]);
      const lonArray = data.map((item) => item.coordinates[0]);

      let maxLat = _.max(latArray);
      let minLat = _.min(latArray);
      let maxLon = _.max(lonArray);
      let minLon = _.min(lonArray);

      if (maxLat === minLat) {
        maxLat += 1;
        minLat -= 1;
      }
      if (maxLon === minLon) {
        maxLon += 0.1;
        minLon -= 0.1;
      }

      latLonRange[key] = { maxLat, minLat, maxLon, minLon };
    });

    const list: Array<CityListItem> = [
      { name: 'All cities', code: 'All cities', display: 'All cities' },
    ];
    cityList.forEach((city) => {
      const display = `${city} (${num[city]})`;
      list.push({
        name: city,
        code: city,
        display,
        latLonRange: latLonRange[city],
      });
    });

    this.cityList = list;
    this.selectedCity = this.cityList[0];
  }

  private setData(): void {
    if (this.users && this.users.length > 0) {
      this.allUserItems = this.users;
      this.allUserItemsFiltered = this.sortUsers(this.users);

      // Get unique company names
      this.companyList = [
        'All Companies',
        ...new Set(
          this.users
            .filter(
              (user) => user.companyName && typeof user.companyName === 'string'
            )
            .map((user) => user.companyName as string)
        ),
      ];
    }
  }

  onSearchInput(term: string): void {
    this.searchTerm$.next(term);
  }

  applySearch(term: string): void {
    if (!term) {
      this.allUserItemsFiltered = this.sortUsers(this.allUserItems);
      return;
    }
    const searchTerm = term.toLowerCase();
    this.allUserItemsFiltered = this.sortUsers(
      this.allUserItems.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm) ||
          user.companyName?.toLowerCase().includes(searchTerm)
      )
    );
  }

  // Keep the original onSearch for compatibility, but deprecate its use
  onSearch(): void {
    this.onSearchInput(this.orgTreeSearch);
  }

  private isFetchingProfile = false;

  fetchProfilesFromOrganization(user: User, event: Event, status: boolean): void {
    event.stopPropagation();
    if (this.isFetchingProfile) {
      return;
    }
    this.isFetchingProfile = true;

    const title = 'User Details';
    this.organizationService.getProfilesFromOrganization(user.userId).pipe(
      switchMap((userInfo: any) => {
        if (!userInfo) {
          return of({ userInfo: null, pictureBlob: null, emergencyNumber: null });
        }

        const pictureId = userInfo?.UserPictures?.[0]?.id || null;
        const organizationId = userInfo?.UserOrganizations?.[0]?.Organization?.id || null;

        return this.organizationService.getEmergencyNumber().pipe(
          switchMap((emergencyNumbers: any) => {
            const emergencyNumber = this.findEmergencyNumber(emergencyNumbers, organizationId);

            if (!pictureId) {
              return of({ userInfo, pictureBlob: null, emergencyNumber });
            }

            return this.organizationService.getUserPicture(pictureId).pipe(
              catchError(error => {
                return of(null);
              }),
              map(pictureBlob => ({ userInfo, pictureBlob, emergencyNumber }))
            );
          })
        );
      })
    ).subscribe({
      next: ({ userInfo, pictureBlob, emergencyNumber }) => {
        if (!userInfo) {
          this.isFetchingProfile = false;
          return;
        }

        userInfo.picture = pictureBlob ? URL.createObjectURL(pictureBlob) : null;
        userInfo.emergencyNumber = emergencyNumber;

        if (status) {
          const res = this.openDetailsModal(title, userInfo, true);
          if (res && typeof res.finally === 'function') {
            res.finally(() => {
              this.isFetchingProfile = false;
            });
          } else {
            this.isFetchingProfile = false;
          }
          return;
        }

        this.handleFlyToUserLocation(userInfo);
        this.isFetchingProfile = false;
      },
      error: (err: any) => {
        console.error('Error fetching user data:', err);
        const errRes = this.errorModal('Error', 'Failed to fetch user data.', true);
        if (errRes && typeof errRes.finally === 'function') {
          errRes.finally(() => {
            this.isFetchingProfile = false;
          });
        } else {
          this.isFetchingProfile = false;
        }
      }
    });
  }

  private findEmergencyNumber(emergencyNumbers: any, organizationId: string | null): string | null {
    if (
      emergencyNumbers &&
      Array.isArray(emergencyNumbers.rows) &&
      organizationId
    ) {
      const match = emergencyNumbers.rows.find((item: any) => item.organizationId === organizationId);
      return match?.number || null;
    }
    return null;
  }

  private handleFlyToUserLocation(userInfo: any): void {
    const coordinates = userInfo?.UserAddresses?.[0]?.position?.coordinates;

    if (Array.isArray(coordinates) && coordinates.length === 2) {
      const [longitude, latitude] = coordinates;
      this.camera.flyToLocation(latitude, longitude, 500);
      this.userInfoRequested.emit(userInfo);
    } else {
      this.errorModal('Location unknown', 'Location unknown', true);
    }
  }

  private openDetailsModal(
    title: string,
    data: any,
    shouldCallApi: boolean
  ) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.userInfo = data;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;


    return modalRef.result;
  }
  private errorModal(title: string, data: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'md',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = data;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }


  public openMultipleMessageModal(
    title: string,
    user: any,
    showCancel: boolean = false
  ) {
    this.organizationService.getProfilesFromOrganization(user.userId).subscribe({
      next: (userInfo: any) => {
        const modalRef = this.modalService.open(MultipleMessageComponent, {
          size: 'md',
          centered: true,
          backdrop: 'static',
          keyboard: false,
        });
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.userInfo = userInfo;
        modalRef.componentInstance.showCancel = showCancel;
        modalRef.componentInstance.users = this.users;
        return modalRef.result;
      },
      error: (err: any) => {
        console.error('Error fetching user profile:', err);
      }
    });
  }
  trackByUserId(index: number, user: User): any {
    return user.userId;
  }
  //   openChatWindow(event: Event, user: User): void {
  //   // Implementation for chat window
  // }
  openChatWindow(title: string, user: any, isNew: boolean) {
    const modalRef = this.modalService.open(MessageComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.user = user;
    modalRef.componentInstance.title = title;
  }
}